import { Loader, Modal } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FirewallSecurityGroupResponse } from '../../../../../../@types/firewall-security-group';
import { Button, InputField, TextArea } from '../../../../../../components/Atoms';
import { useCreateSecurityGroupMutate } from '../../../../../../hooks/CloudComputeHooks/useCreateSecurityGroupMutate';
import { useUpdateSecurityGroupMutate } from '../../../../../../hooks/CloudComputeHooks/useUpdateSecurityGroupMutate';

interface SecurityGroupModalProps {
    opened: boolean;
    onClose: () => void;
    group?: FirewallSecurityGroupResponse;
}

export default function SecurityGroupModal({ opened, onClose, group }: SecurityGroupModalProps) {
    const { t } = useTranslation('cloudCompute');
    const createMutation = useCreateSecurityGroupMutate();
    const updateMutation = useUpdateSecurityGroupMutate();
    const isEdit = !!group;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    useEffect(() => {
        if (!opened) return;
        if (group) {
            setName(group.name);
            setDescription(group.description ?? '');
        } else {
            setName('');
            setDescription('');
        }
    }, [opened, group]);

    const isSaving = createMutation.isPending || updateMutation.isPending;
    const canSubmit = name.trim().length > 0 && !isSaving;

    const handleSave = () => {
        if (!canSubmit) return;
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();
        const payload = {
            name: trimmedName,
            description: trimmedDescription || undefined,
        };

        if (isEdit && group) {
            updateMutation.mutate({ id: group.id, ...payload }, { onSuccess: () => onClose() });
        } else {
            createMutation.mutate(payload, { onSuccess: () => onClose() });
        }
    };

    return (
        <Modal
            size="sm"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {isEdit
                                ? t('securityGroups.renameGroup')
                                : t('securityGroups.createGroupTitle')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {isEdit
                            ? t('securityGroups.renameGroupSubtitle')
                            : t('securityGroups.createGroupSubtitle')}
                    </p>
                </div>
                <div className="w-full flex flex-col justify-start items-start gap-4">
                    <InputField
                        label={t('securityGroups.name')}
                        placeholder={t('securityGroups.namePlaceholder')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isSaving}
                        maxLength={18}
                    />
                    <TextArea
                        label={t('securityGroups.description')}
                        placeholder={t('securityGroups.descriptionPlaceholder')}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        disabled={isSaving}
                    />
                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button width="100%" onClick={handleSave} disabled={!canSubmit}>
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('securityGroups.saving')}</span>
                                </div>
                            ) : isEdit ? (
                                t('securityGroups.saveChanges')
                            ) : (
                                t('securityGroups.createGroup')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            {t('securityGroups.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
